import React from "react";
import AccordionDetails, { accordionDetailsClasses } from "@mui/joy/AccordionDetails";
import AccordionSummary, { accordionSummaryClasses } from "@mui/joy/AccordionSummary";
import { Typography, Box, Accordion, AccordionGroup, Stack } from "@mui/joy";
import PropTypes from "prop-types";

const faqs = [
  {
    q: "Where did you get all of this information?",
    a: "Congress makes information readily available through Congress.gov. You can find information about upcoming bills as well as members of congress.",
  },
  {
    q: "Why is civic engagement important?",
    a: "Civic engagement ensures your voice is heard in the democratic process. When citizens actively participate by contacting representatives and staying informed about legislation, government becomes more responsive and representative of all communities.",
  },
  {
    q: "What is the purpose of this civic engagement website?",
    a: "CivicEngage makes it easy to understand legislation and contact your representatives. We simplify complex bills, help you find your elected officials, and provide ready-to-send message templates — so you can make your voice heard on issues that matter to you.",
  },
  {
    q: "How can I navigate the website effectively?",
    a: "Use the navigation bar at the top to access Federal Bills, State Bills, Legislators, and Resources. Click any bill to view its details, see who sponsored it, and access a tool to contact your representative.",
  },
  {
    q: "How does the website ensure accessibility for all users?",
    a: "We follow web accessibility guidelines to ensure all content is usable for individuals with disabilities. This includes alternative text for images, keyboard navigation, and clear language throughout the site.",
  },
  {
    q: "How can I stay updated on the different bills and legislation?",
    a: "Bookmark this page and search for the latest updates through our Bills & Legislation page. Notification alerts are in development.",
  },
  {
    q: "I don't understand the process the bills go through.",
    a: "Visit our Resources page to learn how bills become laws, the different types of legislation, and how Congress works. We break down the process in plain language so anyone can understand.",
  },
];

const FrequentlyAskedQuestions = ({ goToResources }) => {
  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        width: "100%",
        px: { xs: "1.25rem", md: "2rem" },
        pt: { xs: "3rem", md: "2rem" },
        pb: { xs: "5rem", md: "7rem" },
      }}
    >
      <Stack spacing={1} sx={{ mb: { xs: "3rem", md: "4rem" } }}>
        <Typography
          component="p"
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "var(--ce-dark-eyebrow)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Everything you need to know
        </Typography>
        <Typography
          level="h2"
          sx={{
            color: "white",
            fontSize: { xs: "2rem", md: "3rem" },
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Frequently asked questions
        </Typography>
      </Stack>

      <AccordionGroup
        variant="plain"
        transition="0.2s ease"
        sx={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
          gap: 0,
          "--variant-plainHoverBg": "transparent",
          "--variant-plainActiveBg": "transparent",
          "--variant-plainColor": "white",
          [`& .${accordionSummaryClasses.button}`]: {
            paddingBlock: "1.25rem",
            paddingInline: 0,
            color: "white",
            background: "transparent",
            fontWeight: 600,
            fontSize: "1.125rem",
            "&:hover": {
              background: "transparent",
              color: "rgba(255,255,255,0.85)",
            },
            "&:active": { background: "transparent" },
            "&:focus-visible": {
              outline: "2px solid rgba(255,255,255,0.4)",
              outlineOffset: "2px",
              background: "transparent",
            },
          },
          [`& .${accordionDetailsClasses.content}`]: {
            color: "rgba(255,255,255,0.85)",
            paddingInline: 0,
            fontSize: "1rem",
            lineHeight: 1.65,
          },
          [`& .${accordionDetailsClasses.content}.${accordionDetailsClasses.expanded}`]: {
            paddingBlock: "0 1.25rem",
          },
        }}
      >
        {faqs.map((item, index) => (
          <Accordion key={index} color="neutral">
            {index > 0 && (
              <Box
                sx={{
                  height: "1px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  mx: 0,
                }}
              />
            )}
            <AccordionSummary>
              <Typography
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  lineHeight: 1.55,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {item.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "1rem",
                  lineHeight: 1.65,
                }}
              >
                {item.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionGroup>
    </Box>
  );
};

FrequentlyAskedQuestions.propTypes = {
  goToResources: PropTypes.func.isRequired,
};

export default FrequentlyAskedQuestions;
